/**
 * Login Form Component
 *
 * Email/password login form with validation using react-hook-form + zod
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.js';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  // Read error directly from the auth store via context. auth.store.login
  // sets `error` on failure before re-throwing, so the context is the
  // authoritative source. Making this the render source of truth makes
  // the banner resilient to any LoginForm remount (which may be what was
  // preventing the previous local-state version from rendering).
  const { login, isLoading, error: authError, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();

    try {
      await login(data.email, data.password);
      // Wait a tick to ensure auth state is fully updated
      await new Promise(resolve => setTimeout(resolve, 100));
      onSuccess?.();
    } catch {
      // auth.store.login already set store.error with the server's
      // message; the banner below reads it from useAuth().error.
    }
  };

  // Explicit preventDefault wrapper. handleSubmit from react-hook-form
  // is supposed to call preventDefault internally, but if any part of
  // our handler chain throws before it gets there (or in some browser
  // edge cases), the browser will fall through to native form POST →
  // page reload → all local state (including the error banner) dies.
  // Wrapping guarantees the default action is killed up front.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit(onSubmit)(e);
  };

  return (
    <div className="w-full">
      {/* noValidate prevents any browser-side HTML5 validation quirks
          from interfering with our react-hook-form + zod flow. */}
      <form onSubmit={handleFormSubmit} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            {...register('email')}
            disabled={isLoading}
            autoComplete="email"
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              {...register('password')}
              disabled={isLoading}
              autoComplete="current-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          )}
        </div>

        {authError && (
          <div
            role="alert"
            className="p-3 text-sm rounded-md border"
            style={{
              color: '#991B1B',
              backgroundColor: '#FEF2F2',
              borderColor: '#FECACA',
            }}
          >
            {authError}
          </div>
        )}

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </form>
    </div>
  );
}
